import {
  useMutationMembershipSignupCustomPayment,
  useMutationMembershipSignupPaymentMethod,
  useMutationProductOrderCheckoutCustomPayment,
  useMutationProductOrderCheckoutPaymentMethod,
  useMutationSessionPackSignupCustomPayment,
  useMutationSessionPackSignupPaymentMethod,
} from "@gymflow/api";
import { CustomPaymentCategoryDTO } from "@gymflow/types";
import useGymflowModels from "apps/portal/src/store";
import { useCallback } from "react";

import { SelectInputOptionType } from "../../../atoms";
import { ShopCartType } from "../../ShopTypes";
import { TabsType } from "../ShopSidebarProvider";

type ProcessCheckoutProps = {
  cart: ShopCartType;
  activeTabId: TabsType;
  orderKey: string;
  values: {
    priceOverride?: number;
    inPersonPaymentMethod?: SelectInputOptionType<CustomPaymentCategoryDTO>;
    paymentMethodId?: string;
  };
  startDate: string;
  userMemberId: string;
  promotionCode?: string;
  paymentMethodId?: string;
  priceOverride?: number;
  customPaymentsEnabled: boolean;
};

export const useProcessCheckout = () => {
  const { api } = useGymflowModels();

  const { mutateAsync: membershipSignupCustomPaymentMutateAsync } =
    useMutationMembershipSignupCustomPayment({ api });
  const { mutateAsync: membershipSignupPaymentMethodMutateAsync } =
    useMutationMembershipSignupPaymentMethod({ api });
  const { mutateAsync: productOrderCheckoutCustomPaymentMutateAsync } =
    useMutationProductOrderCheckoutCustomPayment({ api });
  const { mutateAsync: productOrderCheckoutPaymentMethodMutateAsync } =
    useMutationProductOrderCheckoutPaymentMethod({ api });
  const { mutateAsync: sessionPackSignupCustomPaymentMutateAsync } =
    useMutationSessionPackSignupCustomPayment({ api });
  const { mutateAsync: sessionPackSignupPaymentMethodMutateAsync } =
    useMutationSessionPackSignupPaymentMethod({ api });

  const processCheckout = useCallback(
    async ({
      cart,
      activeTabId,
      orderKey,
      values,
      startDate,
      userMemberId,
      promotionCode,
      paymentMethodId,
      priceOverride,
      customPaymentsEnabled,
    }: ProcessCheckoutProps) => {
      let result: { invoiceNumber?: string } = {};

      if (cart.type === "MEMBERSHIP") {
        if (customPaymentsEnabled) {
          if (activeTabId === "IN_PERSON") {
            result = await membershipSignupCustomPaymentMutateAsync({
              membershipId: cart.payload.id,
              startDate,
              orderKey,
              customPaymentCategoryId:
                (values?.inPersonPaymentMethod?.id as number) || 0,
              priceOverride,
              promotionCode,
              userMemberId,
            });
          } else if (activeTabId === "ONLINE") {
            result = await membershipSignupPaymentMethodMutateAsync({
              membershipId: cart.payload.id,
              startDate,
              orderKey,
              paymentMethodId,
              priceOverride,
              promotionCode,
              userMemberId,
            });
          }
        } else {
          result = await membershipSignupPaymentMethodMutateAsync({
            membershipId: cart.payload.id,
            startDate,
            orderKey,
            paymentMethodId,
            priceOverride,
            promotionCode,
            userMemberId,
          });
        }
      } else if (cart.type === "CREDIT_PACK") {
        if (customPaymentsEnabled) {
          if (activeTabId === "IN_PERSON") {
            result = await sessionPackSignupCustomPaymentMutateAsync({
              sessionPackId: cart.payload.id,
              startDate,
              orderKey,
              customPaymentCategoryId:
                (values?.inPersonPaymentMethod?.id as number) || 0,
              priceOverride,
              promotionCode,
              userMemberId,
            });
          } else if (activeTabId === "ONLINE") {
            result = await sessionPackSignupPaymentMethodMutateAsync({
              sessionPackId: cart.payload.id,
              startDate,
              orderKey,
              paymentMethodId,
              priceOverride,
              promotionCode,
              userMemberId,
            });
          }
        } else {
          result = await sessionPackSignupPaymentMethodMutateAsync({
            sessionPackId: cart.payload.id,
            startDate,
            orderKey,
            paymentMethodId,
            priceOverride,
            promotionCode,
            userMemberId,
          });
        }
      } else if (cart.type === "PRODUCT") {
        const products = cart.payload.map((e) => ({
          productId: e.product.id,
          quantity: e.quantity,
        }));
        if (customPaymentsEnabled) {
          if (activeTabId === "IN_PERSON") {
            result = await productOrderCheckoutCustomPaymentMutateAsync({
              orderItems: products,
              orderKey,
              customPaymentCategoryId:
                (values?.inPersonPaymentMethod?.id as number) || 0,
              priceOverride,
              promotionCode,
              userMemberId,
            });
          } else if (activeTabId === "ONLINE") {
            result = await productOrderCheckoutPaymentMethodMutateAsync({
              orderItems: products,
              orderKey,
              paymentMethodId,
              priceOverride,
              promotionCode,
              userMemberId,
            });
          }
        } else {
          result = await productOrderCheckoutPaymentMethodMutateAsync({
            orderItems: products,
            orderKey,
            paymentMethodId,
            priceOverride,
            promotionCode,
            userMemberId,
          });
        }
      }
      return result;
    },
    [
      membershipSignupCustomPaymentMutateAsync,
      membershipSignupPaymentMethodMutateAsync,
      productOrderCheckoutCustomPaymentMutateAsync,
      productOrderCheckoutPaymentMethodMutateAsync,
      sessionPackSignupCustomPaymentMutateAsync,
      sessionPackSignupPaymentMethodMutateAsync,
    ],
  );

  return { processCheckout };
};
